import { Button } from '@localina/core';
import { Modal } from '@mui/material';
import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';

interface IProps {
    welcomeMessageNumber: number;
}

const welcomeModalLocalStorageName = (messageNumber: number) => `isWelcomed${messageNumber}`;

const STROKE_ICON_PROPS = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
} as const;

const CloseGlyph = () => (
    <svg {...STROKE_ICON_PROPS} width={16} height={16} strokeWidth={2.2}>
        <path d="m6 6 12 12M18 6 6 18" />
    </svg>
);
const BrushGlyph = () => (
    <svg {...STROKE_ICON_PROPS} width={20} height={20} strokeWidth={1.8}>
        <path d="M4.5 19.5c1.8 0 3.2-1.4 3.2-3.2 0-1.3-.9-2.4-2.1-2.9" />
        <path d="M7.7 16.3 17.9 6.1a2 2 0 0 0-2.8-2.8L4.9 13.5" />
        <path d="M4.5 19.5c-.9 0-1.6-.4-1.6-.4.6-.6 1-1.3 1-2.2" />
    </svg>
);
const WrenchGlyph = () => (
    <svg {...STROKE_ICON_PROPS} width={19} height={19} strokeWidth={1.8}>
        <path d="M14.8 6.2a4.2 4.2 0 0 0 5.4 5.4l-8 8a2.4 2.4 0 0 1-3.4-3.4Z" />
        <path d="M14.8 6.2 17.2 3.8" />
    </svg>
);
const InfoGlyph = () => (
    <svg {...STROKE_ICON_PROPS} width={17} height={17} strokeWidth={1.8}>
        <circle cx="12" cy="12" r="8.4" />
        <path d="M12 11v5.4M12 8.1v.2" />
    </svg>
);
const ReloadGlyph = () => (
    <svg {...STROKE_ICON_PROPS} width={16} height={16} strokeWidth={1.8}>
        <path d="M20 11.5a8 8 0 1 0-1.6 5.6" />
        <path d="M20 5.4v6h-6" />
    </svg>
);

export const WelcomeModal: React.FC<IProps> = ({ welcomeMessageNumber }) => {
    const { t } = useTranslation();
    const [isWelcomeVisible, setWelcomeVisible] = React.useState(false);

    const markAsSeen = () => localStorage.setItem(welcomeModalLocalStorageName(welcomeMessageNumber), 'true');

    const handleWelcomeModalClose = () => {
        setWelcomeVisible(false);
        markAsSeen();
    };

    const handleReload = () => {
        // Marked first, so the reloaded page doesn't greet the user a second time.
        markAsSeen();
        window.location.reload();
    };

    React.useEffect(() => {
        setWelcomeVisible(!localStorage.getItem(welcomeModalLocalStorageName(welcomeMessageNumber)));

        // clear all previous welcome modal variables from localstorage
        for (let i = 0; i < welcomeMessageNumber; i++) {
            if (localStorage.getItem(welcomeModalLocalStorageName(i))) {
                localStorage.removeItem(welcomeModalLocalStorageName(i));
            }
        }
    }, []);

    return (
        <Modal open={isWelcomeVisible} className="welcome-modal" onClose={handleWelcomeModalClose}>
            <div
                className="welcome-modal__dialog"
                role="dialog"
                aria-modal={true}
                aria-labelledby="welcome-modal-title"
                aria-describedby="welcome-modal-subtitle"
            >
                <div className="welcome-modal__head">
                    <span className="welcome-modal__grab" aria-hidden={true} />
                    <button
                        type="button"
                        className="welcome-modal__close"
                        aria-label={t('welcomeModal.close')}
                        onClick={handleWelcomeModalClose}
                    >
                        <CloseGlyph />
                    </button>
                    <h2 id="welcome-modal-title" className="welcome-modal__title">
                        {t('welcomeModal.title')}
                    </h2>
                    <p id="welcome-modal-subtitle" className="welcome-modal__subtitle">
                        {t('welcomeModal.subtitle')}
                    </p>
                </div>

                <div className="welcome-modal__body">
                    <div className="welcome-modal__item">
                        <span className="welcome-modal__item-icon" aria-hidden={true}>
                            <BrushGlyph />
                        </span>
                        <div>
                            <div className="welcome-modal__item-title">{t('welcomeModal.design.title')}</div>
                            <div className="welcome-modal__item-text">
                                <Trans i18nKey="welcomeModal.design.text" components={{ bold: <b /> }} />
                            </div>
                        </div>
                    </div>
                    <div className="welcome-modal__item">
                        <span className="welcome-modal__item-icon secondary" aria-hidden={true}>
                            <WrenchGlyph />
                        </span>
                        <div>
                            <div className="welcome-modal__item-title">{t('welcomeModal.improvements.title')}</div>
                            <div className="welcome-modal__item-text">{t('welcomeModal.improvements.text')}</div>
                        </div>
                    </div>
                    <div className="welcome-modal__hint">
                        <span className="welcome-modal__hint-icon" aria-hidden={true}>
                            <InfoGlyph />
                        </span>
                        <span>{t('welcomeModal.hint')}</span>
                    </div>
                </div>

                <div className="welcome-modal__foot">
                    <span className="welcome-modal__signature">{t('welcomeModal.signature')}</span>
                    <Button
                        secondary
                        startIcon={<ReloadGlyph />}
                        label={t('welcomeModal.reload')}
                        onClick={handleReload}
                    />
                    <Button label={t('welcomeModal.confirm')} onClick={handleWelcomeModalClose} />
                </div>
            </div>
        </Modal>
    );
};
